import { SECTION_SLUGS } from "./sections";
import { CATEGORY_SLUGS } from "./categories";

// Affordances exposed for a section. true = render, false = hide.
export type SectionAffordances = {
  perField: {
    hide: boolean;             // eye-off icon — hide single field
    notes: boolean;            // file-text icon — Notes & documents popover
    sendToPhone: boolean;      // smartphone icon — SMS upload link
    uploadFromComputer: boolean; // paperclip icon — direct file upload
    addAnother: boolean;       // "+ Add another [FieldName]" link under each field
  };
  section: {
    customize: boolean;        // "Customize" toggle in section header
    hideCategory: boolean;     // "Hide Category" button in section header
    addAnotherSection: boolean; // "Add Another [section name]" button at section bottom
  };
};

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

// Default = current production behavior. Every section behaves identically to
// today unless it has an entry in SECTION_AFFORDANCE_OVERRIDES below.
export const DEFAULT_AFFORDANCES: SectionAffordances = {
  perField: {
    hide: true,
    notes: true,
    sendToPhone: true,
    uploadFromComputer: true,
    addAnother: true,
  },
  section: {
    customize: true,
    hideCategory: true,
    addAnotherSection: true,
  },
};

// Sections suppressed from the category page renderer.
// The section remains active in the DB — this is a UI-layer hide only.
// Use when a section's edit surface has moved to another page (e.g. MessageToExecutorCard).
export const HIDDEN_SECTIONS: Set<string> = new Set([
  SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_EXECUTOR_INSTRUCTIONS,
  // Where Are They? — its UI moved to the standalone "Where Is Everything" category
  // (session 72 RT#2). The section stays active in the DB (the aggregator reads its
  // manual-band entries by slug); only My Wishes & Legacy's renderer suppresses it.
  SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY,
  // s232 (chapter two PR C, ruling 4): Safes and Locked Places — asked only by the interview's Safes screen. Hidden, so
  // no classic page draws a second copy and a new empty section never lowers anyone's progress (section-progress.ts).
  SECTION_SLUGS.ASSETS_SAFES_LOCKED_PLACES,
]);

// Sections whose BODY is rendered by a custom inline card rather than the generic
// field renderer, but which stay first-class in the sidebar tree. The generic
// renderer must skip the generic field body for these; the sidebar (which filters
// only HIDDEN_SECTIONS) shows them normally.
//
// s83: "Your Executor" was migrated to an accordion-INTEGRATED custom body — it is
// now a standard accordion row on the Legal page whose expanded body is
// <YourExecutorCard> (see GenericCategoryPage), so it no longer belongs here. The
// set is currently empty but retained for future inline-card sections.
export const INLINE_CARD_SECTIONS: Set<string> = new Set([]);

// Categories that render all sections expanded by default (no accordion collapse).
// Default behavior for multi-section categories is accordion mode.
// Add a category slug here to opt into always-expanded rendering.
//
// s109: About You was removed from this set. It's a single-section category, so the
// single-section auto-expand effect (GenericCategoryPage) opens it on mount — giving
// the same default-open feel — while restoring a working section-header chevron so the
// planner can collapse the whole card (matching every other category). The collapsible
// field-groups live INSIDE the section body.
export const EXPANDED_CATEGORIES: Set<string> = new Set([]);

export function rendersExpanded(categorySlug: string): boolean {
  return EXPANDED_CATEGORIES.has(categorySlug);
}

// Per-section overrides keyed by section slug. Anything omitted inherits
// from DEFAULT_AFFORDANCES. Only add entries for sections that differ from
// the default — all other sections render identically to today.
const SECTION_AFFORDANCE_OVERRIDES: Record<string, DeepPartial<SectionAffordances>> = {
  [SECTION_SLUGS.ABOUT_YOU]: {
    perField: {
      hide: false,
      notes: true,              // KEEP — Notes & documents popover is the one affordance retained
      sendToPhone: false,
      uploadFromComputer: false,
      addAnother: false,
    },
    section: {
      customize: false,
      hideCategory: false,
      addAnotherSection: false,
    },
  },

  // My Wishes & Legacy active sections — section header buttons removed.
  // perField affordances stay at default for now (design-pass scope).
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY]: {
    section: { customize: false, hideCategory: false, addAnotherSection: false },
  },
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_LETTERS]: {
    section: { customize: false, hideCategory: false, addAnotherSection: false },
  },
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]: {
    section: { customize: false, hideCategory: false, addAnotherSection: false },
  },
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_EXECUTOR_INSTRUCTIONS]: {
    section: { customize: false, hideCategory: false, addAnotherSection: false },
  },

  // s83: "Your Executor" renders a custom card body inside the accordion (no generic
  // fields, data in executor_access) — suppress every field-section affordance so its
  // header row shows only name + chevron + green check, no Customize/Fill/Hide buttons.
  [SECTION_SLUGS.LEGAL_YOUR_EXECUTOR]: {
    perField: { hide: false, notes: false, sendToPhone: false, uploadFromComputer: false, addAnother: false },
    section: { customize: false, hideCategory: false, addAnotherSection: false },
  },
};

// Returns the merged affordances for a section slug.
// Pure function — safe to call in render.
export function getAffordances(slug: string): SectionAffordances {
  const override = SECTION_AFFORDANCE_OVERRIDES[slug];
  if (!override) return DEFAULT_AFFORDANCES;
  return {
    perField: { ...DEFAULT_AFFORDANCES.perField, ...override.perField },
    section: { ...DEFAULT_AFFORDANCES.section, ...override.section },
  };
}
